export const intakeGroups = [
  {
    id: 'group-1',
    introMessage: "Let's do this!",
    intro: {
      title: 'Welcome to WHOLE Life Club!',
      subtitle: 'We’re so glad you’re here.',
      paragraphs: [
        'We’ll tailor your first 8 weeks so you get fast, practical wins.',
        '**This quick form can take under 3 minutes.**',
        'Your answers will personalize your weekly guides, recipes, and emails.',
        '*You can change them at any time.*'
      ],
      buttonText: 'BUILD MY PLAN'
    },
    questions: [
      {
        id: 'priority-track',
        type: 'single',
        required: true,
        title: 'Which of these is your top priority right now?',
        description: 'Pick one track for your first 8 weeks. You can switch tracks at any time in the Member Center.',
        errorMessage: 'Please choose the priority that matters most to you.',
        otherOption: {
          value: 'other',
          required: true,
          prompt: 'These are our only tracks right now, but we’re expanding. What would you like us to add?',
          helperText:
            'For now, you’ll start with the Healthy Eating track (our most comprehensive option). You can change this any time in your member center.',
          placeholder: 'Type your suggestion...',
          maxLength: 80,
          errorMessage: 'Please share the track you’d like us to add.'
        },
        options: [
          { value: 'wlc-track-gut-health', label: 'Gut Health' },
          { value: 'wlc-track-heart-health', label: 'Heart Health' },
          { value: 'wlc-track-brain-health', label: 'Brain Health' },
          { value: 'wlc-track-blood-sugar', label: 'Blood Sugar / Type 2' },
          { value: 'wlc-track-healthy-eating', label: 'Healthy Eating' },
          { value: 'other', label: 'Other', isOther: true }
        ],
        getAffirmation: answer => {
          if (answer?.value === 'other') {
            return answer?.otherText
              ? "Thank you for sharing! We're constantly learning from our members to build the future of WHOLE Life Club."
              : '';
          }
          return 'Great choice! We’ll make your first 8 weeks focused and achievable.';
        }
      },
      {
        id: 'primary-improvement',
        type: 'single',
        required: true,
        title: 'If you could improve just one thing first, what would it be?',
        description: 'This helps us design your first win. As you continue, you’ll build skills that uplift all areas.',
        errorMessage: 'Please choose the reason that matters most to you.',
        options: [
          { value: 'eat-better', label: 'I want to eat better' },
          { value: 'sleep-better', label: 'I want to sleep better' },
          { value: 'stress-less', label: 'I want to stress less' },
          { value: 'move-more', label: 'I want to move more' },
          { value: 'love-connection', label: 'I want more love & connection' }
        ],
        affirmation:
          'That’s great! Knowing your primary motivator is a powerful first step toward achieving your goals.'
      },
      {
        id: 'plan-emphasis',
        type: 'multi',
        required: false,
        maxSelections: 3,
        title: 'Which of these areas should we emphasize in your plan? Pick up to 3.',
        description: 'These help us to further personalize your weekly actions, resources, recipes, and emails.',
        maxErrorMessage: 'Please only pick up to 3 topics.',
        sections: [
          {
            title: 'Health & Body:',
            options: [
              { value: 'weight-metabolism', label: 'Weight & Metabolism' },
              { value: 'physical-activity', label: 'Physical Activity & Movement' },
              { value: 'longevity', label: 'Longevity / Healthy Aging' },
              { value: 'menopause', label: 'Menopause & Perimenopause' }
            ]
          },
          {
            title: 'Food & Cooking:',
            options: [
              { value: 'cooking-skills', label: 'Cooking Skills & Meal Planning' },
              { value: 'quick-easy', label: 'Quick & Easy (time-saving) Meals' },
              { value: 'family-friendly', label: 'Family-friendly / Kid-friendly Eating' }
            ]
          },
          {
            title: 'Mind & Emotions:',
            options: [
              { value: 'stress', label: 'Stress / Mindfulness' },
              { value: 'mental-health', label: 'Mental Health & Mood' },
              { value: 'emotional-eating', label: 'Emotional Eating & Cravings' },
              { value: 'sleep', label: 'Sleep' }
            ]
          },
          {
            title: 'Connection:',
            options: [{ value: 'community', label: 'Community Connection' }]
          }
        ],
        affirmation: 'Nice! Focusing on these areas will be a powerful way to make your new habits stick.'
      },
      {
        id: 'birthday',
        type: 'date',
        required: false,
        title: 'When is your birthday?',
        description:
          "We'd love to celebrate with you and share some birthday-themed recipes! Optional. You can skip or add this later."
      },
      {
        id: 'wfpb-familiarity',
        type: 'single',
        required: false,
        title: 'How familiar are you with whole foods, plant-based eating?',
        description:
          'This helps us match the right difficulty of guides. It’s not a label, and you can change it anytime.',
        options: [
          { value: 'beginner', label: 'Beginner: I’m just getting started' },
          { value: 'intermediate', label: 'Intermediate: I’m on the path and want to uplevel' },
          { value: 'advanced', label: 'Advanced: I’m experienced and want to optimize' }
        ],
        affirmationByValue: {
          beginner:
            'Perfect starting point! We’ve helped thousands of people take simple steps toward transformative health.',
          intermediate:
            'Optimization is where the transformation happens. We’re here to help you move from good to great.',
          advanced:
            'Awesome! We have a wealth of advanced skills and a vibrant community ready to help you reach your next health goal.'
        }
      },
      {
        id: 'commitment-days',
        type: 'single',
        required: false,
        title: 'How many days per week do you want to commit to WHOLE Life Club?',
        description:
          'This could mean logging in, trying a recipe, posting a comment, watching a video, or any small action.',
        options: [
          { value: '1', label: '1 day' },
          { value: '2', label: '2 days' },
          { value: '3', label: '3 days' },
          { value: '4', label: '4 days' },
          { value: '5-plus', label: '5+ days' }
        ],
        affirmation:
          'You got this! Whether it’s 1 day or 7, every step counts toward your transformation. We’re ready to meet you exactly where you are.'
      }
    ]
  },
  {
    id: 'group-2',
    introMessage: "You're halfway done building your plan!",
    intro: {
      title: 'There are just a few more questions to finish personalizing your plan.',
      paragraphs: ['Recipe preferences help us suggest recipes you’ll use and love.'],
      buttonText: 'Set My Recipe Preferences'
    },
    questions: [
      {
        id: 'household-size',
        type: 'single',
        title: 'Who are you cooking for most often?',
        options: [
          { value: '1', label: 'Just me' },
          { value: '2', label: '2 people' },
          { value: '3-4', label: '3–4 people' },
          { value: '5+', label: '5+ people' }
        ],
        affirmation: 'Got it! We’ll prioritize recipes that work for your household size.'
      },
      {
        id: 'shopping-pattern',
        type: 'single',
        title: 'Which best describes how you shop?',
        options: [
          { value: 'monthly', label: 'Once a month (bulk shopping)' },
          { value: 'weekly', label: 'Once a week (bulk shopping)' },
          { value: '2-3-week', label: '2-3 times a week' },
          { value: 'daily', label: 'Daily or almost daily' }
        ],
        affirmation:
          'Thank you! We have hundreds of recipes that will be great for you within your grocery shopping patterns.'
      },
      {
        id: 'add-to-plate',
        type: 'multi',
        maxSelections: 3,
        title: 'Do you want to prioritize adding any of these to your plate? Pick up to 3.',
        options: [
          { value: 'vegetables', label: 'Vegetables' },
          { value: 'legumes', label: 'Legumes / Beans' },
          { value: 'leafy-greens', label: 'Leafy greens' },
          { value: 'whole-grains', label: 'Whole grains' },
          { value: 'nuts-seeds', label: 'Nuts & seeds' },
          { value: 'plant-proteins', label: 'Plant proteins' },
          { value: 'fruit', label: 'Fruit / Berries' },
          { value: 'fermented', label: 'Fermented / Probiotic foods' },
          { value: 'herbs-spices', label: 'Herbs, spices & flavor' }
        ],
        maxErrorMessage: 'Please only pick up to 3 topics.',
        affirmation: 'Delicious! We’ll prioritize recipes featuring these foods.'
      },
      {
        id: 'cooking-skill',
        type: 'single',
        title: 'Which best describes your cooking skill right now?',
        options: [
          { value: 'beginner', label: 'Beginner - I prefer very simple, step-by-step recipes' },
          { value: 'developing', label: 'Developing - I can follow recipes and cook the basics' },
          { value: 'advanced', label: 'Advanced - I enjoy learning techniques and trying new recipes' }
        ],
        affirmation:
          'Love this! Whether you’re just starting out or already comfortable in the kitchen, you’re about to discover how simple and rewarding nourishing yourself can be.'
      },
      {
        id: 'recipe-time',
        type: 'single',
        title: 'How much time do you usually want recipes to take?',
        options: [
          { value: 'under-20', label: 'Under 20 minutes (quick & simple)' },
          { value: '20-40', label: '20–40 minutes (balanced time for great results)' },
          { value: '40-60', label: '40–60 minutes (time for something special)' },
          { value: '60-plus', label: '60+ minutes (I love cooking projects)' },
          { value: 'no-cook', label: 'Minimal/no-cook options' }
        ],
        affirmation: 'Thank you! We’ll prioritize suggesting recipes based on your preferred cooking skill and time.'
      },
      {
        id: 'allergies',
        type: 'multi',
        title: 'Do you have allergies or intolerances to any of these foods?',
        options: [
          { value: 'gluten', label: 'Gluten' },
          { value: 'tree-nuts', label: 'Tree nuts' },
          { value: 'peanuts', label: 'Peanuts' },
          { value: 'soy', label: 'Soy' },
          { value: 'sesame', label: 'Sesame' },
          { value: 'other', label: 'Other (please specify)', isOther: true },
          { value: 'none', label: 'None', isExclusive: true }
        ],
        exclusiveOptions: ['none'],
        otherOption: {
          value: 'other',
          required: true,
          placeholder: 'Type your allergy or intolerance...',
          maxLength: 80,
          errorMessage: 'Please specify your allergy or intolerance.'
        },
        getAffirmation: answer => {
          if (!answer?.value || answer.value.length === 0) return '';
          if (answer.value.includes('none')) {
            return 'Got it! Please update this in your member center if this ever changes.';
          }
          return 'Got it! Your recipe library and Wellness Buddy will generally filter out recipes that contain these ingredients.';
        }
      },
      {
        id: 'avoid-foods',
        type: 'multi',
        title: 'Are there foods you absolutely don’t want to see in your recipes?',
        description: 'Only select if you truly hate these foods, as doing so will lead to more restrictions.',
        options: [
          { value: 'beans', label: 'Beans' },
          { value: 'berries', label: 'Berries' },
          { value: 'carrots', label: 'Carrots' },
          { value: 'cilantro', label: 'Cilantro' },
          { value: 'flaxseeds', label: 'Flaxseeds' },
          { value: 'garlic', label: 'Garlic' },
          { value: 'ginger', label: 'Ginger' },
          { value: 'kale', label: 'Kale' },
          { value: 'lentils', label: 'Lentils' },
          { value: 'mushrooms', label: 'Mushrooms' },
          { value: 'nutritional-yeast', label: 'Nutritional Yeast' },
          { value: 'nuts', label: 'Nuts' },
          { value: 'onions', label: 'Onions' },
          { value: 'quinoa', label: 'Quinoa' },
          { value: 'red-peppers', label: 'Red Bell Peppers' },
          { value: 'turmeric', label: 'Turmeric' },
          { value: 'added-salt', label: 'Added salt' },
          { value: 'added-oil', label: 'Added oil' },
          { value: 'other', label: 'Other (please specify)', isOther: true },
          { value: 'open', label: 'No, I’m open to all of these.', isExclusive: true }
        ],
        exclusiveOptions: ['open'],
        otherOption: {
          value: 'other',
          required: true,
          placeholder: 'Type a food to avoid...',
          maxLength: 80,
          errorMessage: 'Please specify the food you want to avoid.'
        },
        affirmation:
          'Thank you! Your recipe library and Wellness Buddy will prioritize results that respect these choices.',
        nextText: 'Finish Building My Plan'
      }
    ]
  }
];

export const finalScreen = {
  title: 'Thank you!',
  subtitle: 'Your first 8 weeks are now customized.',
  paragraphs: [
    'You’ll receive a personalized kick-off email shortly. Update your preferences at any time in your Member Center.',
    'Ready to dive in? Visit your personlized guide using the button below to start your WHOLE Life journey and learn how to get the most out of your Member Center.',
    '**Let’s do this!**'
  ],
  primaryButtonText: 'Go to My Personalized Guide'
};

export const buildIntakeSteps = groups => {
  const steps = [];
  groups.forEach(group => {
    steps.push({
      type: 'intro',
      groupId: group.id,
      title: group.intro.title,
      subtitle: group.intro.subtitle,
      paragraphs: group.intro.paragraphs,
      buttonText: group.intro.buttonText
    });
    group.questions.forEach(question => {
      steps.push({
        type: 'question',
        groupId: group.id,
        questionId: question.id
      });
    });
  });
  steps.push({ type: 'final' });
  return steps;
};
